import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { adminApi, fmt } from "../../lib/shopApi";

const STATUS_COLORS = {
  new: "border-sky-400/40 text-sky-300",
  awaiting_payment: "border-amber-400/40 text-amber-300",
  pending_payment: "border-amber-400/40 text-amber-300",
  paid: "border-green-400/40 text-green-300",
  preparing: "border-[#C9A24B]/50 text-[#C9A24B]",
  packed: "border-green-400/40 text-green-300",
  shipped: "border-violet-400/40 text-violet-300",
  delivered: "border-[#C9A24B]/50 text-[#C9A24B]",
  cancelled: "border-red-400/40 text-red-300",
  payment_failed: "border-red-400/40 text-red-300",
  payment_error: "border-red-400/40 text-red-300",
};

const OrderCard = ({ order, statuses, onStatusChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-[#232323] border border-[#C9A24B]/10 rounded-2xl overflow-hidden" data-testid={`admin-order-${order.order_number}`}>
      <button onClick={() => setOpen(!open)} className="w-full p-5 flex flex-wrap items-center gap-3 text-left">
        <div className="flex-1 min-w-[160px]">
          <p className="font-serif text-lg text-[#C9A24B]">{order.order_number}</p>
          <p className="text-[#F5EFE3]/55 text-sm">{order.customer.name} · {order.items.reduce((s, i) => s + i.qty, 0)} item(s) · {fmt(order.total_cents)}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${STATUS_COLORS[order.status] || "border-white/20 text-white/50"}`} data-testid={`order-status-${order.order_number}`}>
          {order.status.replace(/_/g, " ")}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-[#F5EFE3]/40" /> : <ChevronDown className="w-4 h-4 text-[#F5EFE3]/40" />}
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-[#C9A24B]/10 pt-4 space-y-4">
          <div className="space-y-1.5">
            {order.items.map((i, idx) => (
              <p key={idx} className="text-sm text-[#F5EFE3]/70">
                {i.name}{i.size_label ? ` (${i.size_label})` : ""} × {i.qty} — {fmt(i.line_total_cents)}
                {Object.values(i.options || {}).length > 0 && (
                  <span className="block text-[#C9A24B]/70 text-xs mt-0.5">
                    {[i.options.length, i.options.color, i.options.thickness].filter(Boolean).join(" · ")}
                  </span>
                )}
              </p>
            ))}
            {order.has_made_to_order && (
              <p className="text-xs text-[#C9A24B] uppercase tracking-widest">Made to order — {order.lead_time_text || "7-15 days"} lead time</p>
            )}
            {order.discount_cents > 0 && <p className="text-sm text-[#C9A24B]">Discount ({order.discount_code}): -{fmt(order.discount_cents)}</p>}
            <p className="text-sm text-[#F5EFE3]/50">Delivery: {order.delivery_method === "collection" ? "Collection (free)" : `Paxi ${fmt(order.delivery_fee_cents)}`}</p>
          </div>

          <div className="text-sm text-[#F5EFE3]/60">
            <p className="text-[#C9A24B] text-xs uppercase tracking-widest mb-1.5">Customer</p>
            <p>{order.customer.name}</p>
            <p>{order.customer.phone} · {order.customer.email}</p>
            {order.customer.address && (
              <p className="mt-1">{order.customer.address.street}, {order.customer.address.city}, {order.customer.address.province}, {order.customer.address.postal}</p>
            )}
          </div>

          <div>
            <p className="text-[#C9A24B] text-xs uppercase tracking-widest mb-2">Change Status</p>
            <select
              value={order.status}
              onChange={(e) => onStatusChange(order.id, e.target.value)}
              data-testid={`status-select-${order.order_number}`}
              className="bg-[#1C1C1C] border border-[#C9A24B]/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C9A24B]"
            >
              {statuses.map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export const OrdersTab = () => {
  const [orders, setOrders] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = (status = filter) =>
    adminApi.get("/orders", { params: { status } }).then((r) => {
      setOrders(r.data.orders);
      setStatuses(r.data.statuses);
    }).catch(() => setOrders([]));

  useEffect(() => { load("all"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (id, status) => {
    await adminApi.patch(`/orders/${id}`, { status });
    load();
  };

  const applyFilter = (s) => {
    setFilter(s);
    load(s);
  };

  if (!orders) return <p className="text-[#F5EFE3]/40 text-sm py-10 text-center">Loading orders…</p>;

  return (
    <div data-testid="orders-tab">
      <div className="flex gap-2 overflow-x-auto pb-3 mb-4">
        {["all", "awaiting_payment", "paid", "preparing", "packed", "shipped", "delivered", "cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => applyFilter(s)}
            data-testid={`order-filter-${s}`}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest whitespace-nowrap border transition-colors ${
              filter === s ? "bg-[#C9A24B] text-[#1C1C1C] border-[#C9A24B] font-bold" : "border-[#C9A24B]/20 text-[#F5EFE3]/55"
            }`}
          >
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {orders.length === 0 ? (
        <p className="text-[#F5EFE3]/40 text-sm py-10 text-center" data-testid="orders-empty">No orders here yet.</p>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <OrderCard key={o.id} order={o} statuses={statuses} onStatusChange={changeStatus} />
          ))}
        </div>
      )}
    </div>
  );
};
